// Apolo IDE — Hermes run formatters (P1-E1-T08).
//
// LOCAL status map for the 7 hermes run states. agentFormat's statusTone
// speaks the dispatch lifecycle (running/completed/failed_timeout/...) and
// must not learn hermes states (shared file, zero-diff discipline) — so the
// run board's map lives here. Every entry is chip-ready: icon + color + LABEL,
// never color-only (doctrine: identity comes from icon+text, not hue).
//
// fmtTokens/fmtDuration/elapsedSince are re-exported from agentFormat
// (import-in-place — one implementation, no copies).

import { Check, CircleDot, CircleOff, Clock, GitFork, GitMerge, HelpCircle, X } from 'lucide-react';
import { elapsedSince, fmtDuration } from '../code/agents/agentFormat.js';

export { fmtTokens, fmtDuration, elapsedSince } from '../code/agents/agentFormat.js';

// Mirrors the API's TERMINAL set (apolo-api hermes runUseCases isTerminal).
const TERMINAL = new Set(['done', 'failed', 'cancelled']);

const STATE_META = {
  queued: { label: 'na fila', tone: 'text-apolo-textTertiary', Icon: Clock },
  running: { label: 'rodando', tone: 'text-apolo-accent', Icon: CircleDot, pulse: true },
  fanning_out: { label: 'fan-out', tone: 'text-apolo-info', Icon: GitFork },
  merging: { label: 'merge', tone: 'text-apolo-info', Icon: GitMerge },
  done: { label: 'ok', tone: 'text-apolo-success', Icon: Check },
  failed: { label: 'falhou', tone: 'text-apolo-error', Icon: X },
  cancelled: { label: 'cancelado', tone: 'text-apolo-warning', Icon: CircleOff },
};

export const HERMES_RUN_STATES = Object.keys(STATE_META);

/**
 * Status chip meta for a run state: { label (pt-BR), tone (tailwind classes
 * over --apolo-* tokens), Icon (lucide), pulse? }. Unknown states degrade to
 * a neutral chip that keeps the raw state name as label (no silent lying).
 */
export function runState(state) {
  const meta = STATE_META[state];
  if (meta) return meta;
  return {
    label: typeof state === 'string' && state ? state : 'desconhecido',
    tone: 'text-apolo-textTertiary',
    Icon: HelpCircle,
    pulse: false,
  };
}

/** done | failed | cancelled → true; everything else (incl. junk) → false. */
export function isTerminalRunState(state) {
  return TERMINAL.has(state);
}

/**
 * HONEST chip meta for a whole run record (E1/E5) — not just its state string.
 * Overrides two misleading cases so the board never lies:
 *   - done + acted=0  → "sem ações" (the run narrated but did nothing — e.g. a
 *     toolless cloud run). NEUTRAL/warning tone, never success-green.
 *   - failed + error 'runner_lost' → "runner caiu" (the reaper finalized an
 *     orphan whose Mac went offline mid-run) — neutral, not an alarming red.
 * Everything else defers to runState(run.state).
 */
export function runOutcomeMeta(run) {
  if (!run) return runState(undefined);
  if (run.state === 'done' && (run.acted === 0 || run.acted === false)) {
    return { label: 'sem ações', tone: 'text-apolo-warning', Icon: CircleOff, pulse: false };
  }
  if (run.state === 'failed' && run.error === 'runner_lost') {
    return { label: 'runner caiu', tone: 'text-apolo-textTertiary', Icon: CircleOff, pulse: false };
  }
  return runState(run.state);
}

/**
 * Elapsed label for a run/child row: terminal → full span (startedAt →
 * finishedAt), live → since start (falls back to createdAt for queued rows).
 * null when there is nothing to measure (missing/garbage timestamps).
 */
export function runElapsedLabel(run) {
  if (!run) return null;
  const ms = isTerminalRunState(run.state)
    ? elapsedSince(run.startedAt, run.finishedAt)
    : elapsedSince(run.startedAt || run.createdAt);
  return ms == null ? null : fmtDuration(ms);
}

/** Finished children of a fan-out: total - nonTerminal (0 when unknown). */
export function doneCount(progress) {
  return progress && typeof progress.total === 'number'
    ? progress.total - (progress.nonTerminal ?? 0)
    : 0;
}
